import React, { useState, useEffect } from 'react';
import {
    Card, Button, Modal, Input, Select, Switch, message,
    Space, Tag, Popconfirm, Tooltip, Badge
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, HolderOutlined,
    SaveOutlined, AppstoreOutlined
} from '@ant-design/icons';
import axios from '../../utils/axios';
import util from '../../utils/util';

const TYPE_COLORS = { ADMIN:'purple', CLIENT:'blue', BOTH:'green' };
const EMOJI_SUGGESTIONS = ['📋','📊','📈','📉','🏥','👤','👥','⚙️','🔧','🔐','🗓','📅','📝','✅','☑️','🎯','⭐','🏆','🎖️','📍','📖','📄','📂','🔍','🔗','🌐','✏️','⚖️','🛡','📥','💊','🏢','🔨','🗂️'];

function IconPicker({ value, onChange }) {
    const [open, setOpen] = useState(false);
    return (
        <div style={{ position:'relative' }}>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <div onClick={() => setOpen(!open)} style={{
                    width:42, height:42, border:'1px solid #d9d9d9', borderRadius:8,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:22, cursor:'pointer', background:'#fafafa'
                }}>{value || '📄'}</div>
                <Input value={value} onChange={e => onChange(e.target.value)}
                    placeholder="Emoji or text icon" style={{ flex:1 }} />
            </div>
            {open && (
                <div style={{
                    position:'absolute', top:48, left:0, zIndex:1000,
                    background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
                    padding:8, display:'flex', flexWrap:'wrap', gap:4,
                    width:280, boxShadow:'0 4px 16px rgba(0,0,0,0.12)'
                }}>
                    {EMOJI_SUGGESTIONS.map(e => (
                        <div key={e} onClick={() => { onChange(e); setOpen(false); }}
                            style={{ width:36, height:36, display:'flex', alignItems:'center',
                                justifyContent:'center', fontSize:20, cursor:'pointer',
                                borderRadius:6, background: value===e ? '#e6f4ff' : '#f5f5f5' }}>
                            {e}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ItemModal({ open, onClose, onSave, item, groups }) {
    const blank = { name:'', key:'', nav_path:'', nav_icon:'📄', group_id:'', type:'BOTH', is_national:0, status:1 };
    const [form, setForm] = useState(blank);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setForm(item ? { ...item } : blank); }, [item, open]);
    const set = (k, v) => setForm(f => ({ ...f, [k]:v }));

    const handleSave = async () => {
        if (!form.name || !form.key || !form.group_id) { message.error('Name, key and group are required'); return; }
        setSaving(true);
        try { await onSave(form); onClose(); }
        catch(e) { message.error(e.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    return (
        <Modal open={open} onCancel={onClose} title={item ? 'Edit Nav Item' : 'Add Nav Item'}
            footer={[
                <Button key="c" onClick={onClose}>Cancel</Button>,
                <Button key="s" type="primary" loading={saving} icon={<SaveOutlined/>} onClick={handleSave}>Save</Button>
            ]} width={560}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:16 }}>
                <div style={{ gridColumn:'1/-1' }}>
                    <label style={{ display:'block', fontWeight:600, marginBottom:4, fontSize:12 }}>Display Name *</label>
                    <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Assessments" />
                </div>
                <div>
                    <label style={{ display:'block', fontWeight:600, marginBottom:4, fontSize:12 }}>Module Key *</label>
                    <Input value={form.key} onChange={e => set('key', e.target.value.replace(/\s/g,'_').toLowerCase())} placeholder="e.g. qoc_assess" />
                </div>
                <div>
                    <label style={{ display:'block', fontWeight:600, marginBottom:4, fontSize:12 }}>Group *</label>
                    <Select value={form.group_id || undefined} onChange={v => set('group_id', v)} style={{ width:'100%' }} placeholder="Select group">
                        {groups.map(g => <Select.Option key={g.id} value={g.id}>{g.title}</Select.Option>)}
                    </Select>
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                    <label style={{ display:'block', fontWeight:600, marginBottom:4, fontSize:12 }}>Nav Path</label>
                    <Input value={form.nav_path} onChange={e => set('nav_path', e.target.value)} placeholder="/qoc/assessments" />
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                    <label style={{ display:'block', fontWeight:600, marginBottom:4, fontSize:12 }}>Icon</label>
                    <IconPicker value={form.nav_icon} onChange={v => set('nav_icon', v)} />
                </div>
                <div>
                    <label style={{ display:'block', fontWeight:600, marginBottom:4, fontSize:12 }}>Visibility Type</label>
                    <Select value={form.type} onChange={v => set('type', v)} style={{ width:'100%' }}>
                        <Select.Option value="BOTH">Both (Admin + Facility)</Select.Option>
                        <Select.Option value="ADMIN">Admin Only</Select.Option>
                        <Select.Option value="CLIENT">Facility Only</Select.Option>
                    </Select>
                </div>
                <div>
                    <label style={{ display:'block', fontWeight:600, marginBottom:4, fontSize:12 }}>National Only</label>
                    <Switch checked={!!form.is_national} onChange={v => set('is_national', v?1:0)} checkedChildren="National" unCheckedChildren="All" />
                </div>
                <div>
                    <label style={{ display:'block', fontWeight:600, marginBottom:4, fontSize:12 }}>Status</label>
                    <Switch checked={!!form.status} onChange={v => set('status', v?1:0)} checkedChildren="Active" unCheckedChildren="Inactive" />
                </div>
            </div>
        </Modal>
    );
}

function GroupModal({ open, onClose, onSave, group }) {
    const [title, setTitle] = useState('');
    const [saving, setSaving] = useState(false);
    useEffect(() => { setTitle(group?.title || ''); }, [group, open]);
    const handleSave = async () => {
        if (!title.trim()) { message.error('Group name is required'); return; }
        setSaving(true);
        try { await onSave({ ...group, title }); onClose(); }
        catch(e) { message.error(e.message || 'Save failed'); }
        finally { setSaving(false); }
    };
    return (
        <Modal open={open} onCancel={onClose} title={group?.id ? 'Edit Group' : 'Add Group'}
            footer={[
                <Button key="c" onClick={onClose}>Cancel</Button>,
                <Button key="s" type="primary" loading={saving} icon={<SaveOutlined/>} onClick={handleSave}>Save</Button>
            ]} width={400}>
            <div style={{ marginTop:16 }}>
                <label style={{ display:'block', fontWeight:600, marginBottom:6, fontSize:12 }}>Group Name *</label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Quality of Care" />
            </div>
        </Modal>
    );
}

export default function QoCNavManager() {
    const [groups,      setGroups]      = useState([]);
    const [modules,     setModules]     = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [itemModal,   setItemModal]   = useState(false);
    const [groupModal,  setGroupModal]  = useState(false);
    const [editItem,    setEditItem]    = useState(null);
    const [editGroup,   setEditGroup]   = useState(null);
    const [dragFrom,    setDragFrom]    = useState(null);

    // Permission checks — inside component so they read from localStorage after login
    const canAdd    = util.canAdd('qoc_nav_manager');
    const canEdit   = util.canEdit('qoc_nav_manager');
    const canDelete = util.canDelete('qoc_nav_manager');

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get('qoc/nav/items');
            const data = res.data?.data || {};
            setGroups(data.groups || []);
            setModules(data.modules || []);
        } catch { message.error('Failed to load nav items'); }
        finally { setLoading(false); }
    };

    const saveItem = async (form) => {
        const res = await axios.post('qoc/nav/save-item', form);
        if (res.data?.code !== 1) throw new Error(res.data?.message || 'Save failed');
        message.success('Saved');
        await load();
    };

    const deleteItem = async (id) => {
        await axios.post(`qoc/nav/delete-item/${id}`);
        message.success('Deleted');
        await load();
    };

    const saveGroup = async (form) => {
        const res = await axios.post('qoc/nav/save-group', form);
        if (res.data?.code !== 1) throw new Error(res.data?.message || 'Save failed');
        message.success('Saved');
        await load();
    };

    const deleteGroup = async (id) => {
        await axios.post(`qoc/nav/delete-group/${id}`);
        message.success('Deleted');
        await load();
    };

    const saveOrder = async (groupId, orderedIds) => {
        try {
            await axios.post('qoc/nav/reorder', { group_id: groupId, order: orderedIds });
            message.success('Order saved');
        } catch { message.error('Failed to save order'); }
    };

    const handleDrop = (groupId, fromIdx, toIdx) => {
        if (fromIdx === toIdx || fromIdx === null) return;
        const groupItems = modules
            .filter(m => m.group_id === groupId)
            .sort((a, b) => (a.nav_order||0) - (b.nav_order||0));
        const reordered = [...groupItems];
        const [moved] = reordered.splice(fromIdx, 1);
        reordered.splice(toIdx, 0, moved);
        const newMods = modules.map(m => {
            const idx = reordered.findIndex(r => r.id === m.id);
            if (idx >= 0) return { ...m, nav_order: idx + 1 };
            return m;
        });
        setModules(newMods);
        saveOrder(groupId, reordered.map(r => r.id));
    };

    const groupedModules = groups.reduce((acc, g) => {
        acc[g.id] = modules
            .filter(m => m.group_id === g.id)
            .sort((a, b) => (a.nav_order||0) - (b.nav_order||0));
        return acc;
    }, {});

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>Left Navigation Manager</h2></div>
            </div>
            <div className="page-pad">
                <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginBottom:16 }}>
                    {canAdd && (
                        <Button icon={<PlusOutlined/>} onClick={() => { setEditGroup(null); setGroupModal(true); }}>
                            Add Group
                        </Button>
                    )}
                    {canAdd && (
                        <Button type="primary" icon={<PlusOutlined/>} onClick={() => { setEditItem(null); setItemModal(true); }}>
                            Add Nav Item
                        </Button>
                    )}
                </div>

                {loading ? (
                    <div style={{ textAlign:'center', padding:40, color:'#888' }}>Loading...</div>
                ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(420px, 1fr))', gap:16 }}>
                        {groups.map(group => {
                            const items = groupedModules[group.id] || [];
                            return (
                                <Card key={group.id} size="small"
                                    title={
                                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                                <AppstoreOutlined style={{ color:'#f59e0b' }} />
                                                <span style={{ fontWeight:700 }}>{group.title}</span>
                                                <Badge count={items.length} style={{ background:'#6b7280' }} />
                                            </div>
                                            <Space>
                                                {canEdit && (
                                                    <Tooltip title="Edit group">
                                                        <Button size="small" icon={<EditOutlined/>}
                                                            onClick={() => { setEditGroup(group); setGroupModal(true); }} />
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Popconfirm title="Delete this group? All items must be removed first."
                                                        onConfirm={() => deleteGroup(group.id)} okText="Delete" okType="danger">
                                                        <Button size="small" danger icon={<DeleteOutlined/>}
                                                            disabled={items.length > 0} />
                                                    </Popconfirm>
                                                )}
                                            </Space>
                                        </div>
                                    }
                                    bodyStyle={{ padding:'8px 12px' }}>
                                    {items.length === 0 && (
                                        <div style={{ color:'#aaa', fontSize:12, padding:'8px 0' }}>No items.</div>
                                    )}
                                    {items.map((item, idx) => (
                                        <div key={item.id}
                                            draggable={canEdit}
                                            onDragStart={() => setDragFrom(idx)}
                                            onDragOver={e => e.preventDefault()}
                                            onDrop={() => handleDrop(group.id, dragFrom, idx)}
                                            style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0',
                                                borderBottom:'1px solid #f0f0f0', cursor: canEdit ? 'default' : 'not-allowed' }}>
                                            {canEdit && <HolderOutlined style={{ color:'#ccc', cursor:'grab', fontSize:16, flexShrink:0 }} />}
                                            <div style={{ fontSize:18, width:28, textAlign:'center', flexShrink:0 }}>
                                                {item.nav_icon || '📄'}
                                            </div>
                                            <div style={{ flex:1, minWidth:0 }}>
                                                <div style={{ fontWeight:600, fontSize:13, color: item.status ? '#1a1a1a' : '#aaa' }}>
                                                    {item.name}
                                                    {!item.status && <Tag style={{ marginLeft:6, fontSize:10 }} color="default">Inactive</Tag>}
                                                </div>
                                                <div style={{ fontSize:11, color:'#888', fontFamily:'monospace' }}>
                                                    {item.nav_path || <span style={{ color:'#ccc' }}>no path</span>}
                                                </div>
                                            </div>
                                            <div style={{ display:'flex', gap:4, alignItems:'center', flexShrink:0 }}>
                                                <Tag color={TYPE_COLORS[item.type]||'default'} style={{ fontSize:10, margin:0 }}>{item.type}</Tag>
                                                {!!item.is_national && <Tag color="orange" style={{ fontSize:10, margin:0 }}>Natl</Tag>}
                                                {canEdit && (
                                                    <Button size="small" icon={<EditOutlined/>}
                                                        onClick={() => { setEditItem(item); setItemModal(true); }} />
                                                )}
                                                {canDelete && (
                                                    <Popconfirm title={`Delete "${item.name}"?`}
                                                        onConfirm={() => deleteItem(item.id)} okText="Delete" okType="danger">
                                                        <Button size="small" danger icon={<DeleteOutlined/>} />
                                                    </Popconfirm>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </Card>
                            );
                        })}
                    </div>
                )}

                <ItemModal open={itemModal} onClose={() => setItemModal(false)}
                    onSave={saveItem} item={editItem} groups={groups} />
                <GroupModal open={groupModal} onClose={() => setGroupModal(false)}
                    onSave={saveGroup} group={editGroup} />
            </div>
        </div>
    );
}
